import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import useGetAllBadgeHistory from "../../hooks/useGetAllBadgeHistory";
import Spinner from "../spinner/Spinner";
import styles from "./BadgeHistoryListStyles.module.css";

type BadgeHistoryEntry = {
  postId: number;
  recipientId: number;
  badgeId: number;
  badgeName: string;
  badgeImageURL: string | null;
  sender: {
    id: number;
    preferredName: string;
    lastName: string;
    email: string;
  } | null;
  recipient: {
    id: number;
    preferredName: string;
    lastName: string;
  };
  message: string;
  createdAt: string;
};

const BadgeHistoryList = () => {
  const { ref, inView } = useInView();
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useGetAllBadgeHistory();

  const historyList: BadgeHistoryEntry[] =
    data?.pages.flatMap((page: { badgeHistory: BadgeHistoryEntry[] }) => page.badgeHistory) || [];

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, isFetchingNextPage, hasNextPage, fetchNextPage]);

  return (
    <div className={styles.manageAddOnListBlk}>
      <div className={styles.manageProductBlkTitle}>
        <h1>Badge History</h1>
      </div>

      <div className={styles.addOnListMainContainer}>
        {isLoading ? (
          <div style={{ display: "block", padding: "10px 0" }}>
            <Spinner variant="medium-spinner" alignSpin="center-spinner" />
          </div>
        ) : historyList.length === 0 ? (
          <p className={styles.notAvailableBlkTxt}>
            No badge history records available
          </p>
        ) : (
          <table className={styles.accessibleTable} aria-label="Badge History">
            <thead>
              <tr>
                <th scope="col">Badge</th>
                <th scope="col">Given By</th>
                <th scope="col">Recipient</th>
                <th scope="col">Date</th>
              </tr>
            </thead>
            <tbody>
              {historyList.map((entry: BadgeHistoryEntry) => (
                <tr key={`${entry.postId}-${entry.recipientId}`}>
                  <td>
                    <div className={styles.badgeCell}>
                      {entry.badgeImageURL && (
                        <img
                          src={entry.badgeImageURL}
                          alt={entry.badgeName}
                          className={styles.badgeImage}
                        />
                      )}
                      <span className={styles.badgeName}>{entry.badgeName}</span>
                    </div>
                  </td>
                  <td>
                    {entry.sender
                      ? `${entry.sender.preferredName || ""} ${entry.sender.lastName || ""}`.trim()
                      : "-"}
                  </td>
                  <td>
                    {entry.recipient
                      ? `${entry.recipient.preferredName || ""} ${entry.recipient.lastName || ""}`.trim()
                      : "-"}
                  </td>
                  <td>
                    {entry.createdAt
                      ? new Date(entry.createdAt).toLocaleString()
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div ref={ref} style={{ height: "1px" }}></div>
        {isFetchingNextPage && (
          <div style={{ display: "block", padding: "20px 0px" }}>
            <Spinner variant="medium-spinner" alignSpin="center-spinner" />
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeHistoryList;
